
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { GripVertical, Edit2, Eye, EyeOff, Save, X, ArrowUp, ArrowDown } from 'lucide-react';
import { menuItems } from '../sidebar/menuItems';

interface MenuItemType {
  id: string;
  title: string;
  visible: boolean;
  icon: any;
  color: string;
  url?: string;
  subItems?: any[];
}

interface ItemProps {
  item: MenuItemType;
  index: number;
  totalItems: number;
  onToggleVisibility: (id: string) => void;
  onEditTitle: (id: string, newTitle: string) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
}

function MenuItem({ item, index, totalItems, onToggleVisibility, onEditTitle, onMoveUp, onMoveDown }: ItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title);

  const handleSaveTitle = () => {
    onEditTitle(item.id, editTitle);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditTitle(item.title);
    setIsEditing(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onMoveUp(index)}
              disabled={index === 0}
              className="h-6 w-6 p-0"
            >
              <ArrowUp className="w-3 h-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onMoveDown(index)}
              disabled={index === totalItems - 1}
              className="h-6 w-6 p-0"
            >
              <ArrowDown className="w-3 h-3" />
            </Button>
          </div>
          
          <div className={`w-10 h-10 bg-gradient-to-r ${item.color} rounded-lg flex items-center justify-center shadow-md`}>
            <item.icon className="w-5 h-5 text-white" />
          </div>
          
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-48"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle();
                  if (e.key === 'Escape') handleCancelEdit();
                }}
                autoFocus
              />
              <Button size="sm" variant="outline" onClick={handleSaveTitle}>
                <Save className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <span className="font-medium text-slate-700 flex-1">{item.title}</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
              title="تعديل الاسم"
            >
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => onToggleVisibility(item.id)}
            title={item.visible ? "إخفاء" : "إظهار"}
          >
            {item.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function MenuReorderSection() {
  const [items, setItems] = useState<MenuItemType[]>([]);

  useEffect(() => {
    // Load saved menu order or use default
    const savedOrder = localStorage.getItem('menu-order');
    const savedVisibility = localStorage.getItem('menu-visibility');
    const savedTitles = localStorage.getItem('menu-titles');

    let initialItems = menuItems.map((item, index) => ({
      id: `menu-${index}`,
      title: item.title,
      visible: true,
      icon: item.icon,
      color: item.color,
      url: item.url,
      subItems: item.subItems
    }));

    if (savedOrder) {
      try {
        const orderArray = JSON.parse(savedOrder);
        const orderedItems = orderArray.map((id: string) => 
          initialItems.find(item => item.id === id)
        ).filter(Boolean);
        initialItems = orderedItems;
      } catch (e) {
        console.error('Error parsing saved order:', e);
      }
    }

    if (savedVisibility) {
      try {
        const visibilityMap = JSON.parse(savedVisibility);
        initialItems = initialItems.map(item => ({
          ...item,
          visible: visibilityMap[item.id] !== false
        }));
      } catch (e) {
        console.error('Error parsing saved visibility:', e);
      }
    }

    if (savedTitles) {
      try {
        const titlesMap = JSON.parse(savedTitles);
        initialItems = initialItems.map(item => ({
          ...item,
          title: titlesMap[item.id] || item.title
        }));
      } catch (e) {
        console.error('Error parsing saved titles:', e);
      }
    }

    setItems(initialItems);
  }, []);

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    
    setItems(prev => {
      const newItems = [...prev];
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      
      // Save order to localStorage
      localStorage.setItem('menu-order', JSON.stringify(newItems.map(item => item.id)));
      
      return newItems;
    });
  };

  const handleMoveDown = (index: number) => {
    if (index === items.length - 1) return;
    
    setItems(prev => {
      const newItems = [...prev];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      
      // Save order to localStorage
      localStorage.setItem('menu-order', JSON.stringify(newItems.map(item => item.id)));
      
      return newItems;
    });
  };

  const handleToggleVisibility = (id: string) => {
    setItems(items => {
      const newItems = items.map(item => 
        item.id === id ? { ...item, visible: !item.visible } : item
      );
      
      // Save visibility to localStorage
      const visibilityMap = newItems.reduce((acc, item) => {
        acc[item.id] = item.visible;
        return acc;
      }, {} as Record<string, boolean>);
      localStorage.setItem('menu-visibility', JSON.stringify(visibilityMap));
      
      return newItems;
    });
  };

  const handleEditTitle = (id: string, newTitle: string) => {
    setItems(items => {
      const newItems = items.map(item => 
        item.id === id ? { ...item, title: newTitle } : item
      );
      
      // Save titles to localStorage
      const titlesMap = newItems.reduce((acc, item) => {
        acc[item.id] = item.title;
        return acc;
      }, {} as Record<string, string>);
      localStorage.setItem('menu-titles', JSON.stringify(titlesMap));
      
      return newItems;
    });
  };

  const resetToDefault = () => {
    localStorage.removeItem('menu-order');
    localStorage.removeItem('menu-visibility');
    localStorage.removeItem('menu-titles');
    
    const defaultItems = menuItems.map((item, index) => ({
      id: `menu-${index}`,
      title: item.title,
      visible: true,
      icon: item.icon,
      color: item.color,
      url: item.url,
      subItems: item.subItems
    }));
    
    setItems(defaultItems);
    // Reload page to apply changes
    setTimeout(() => window.location.reload(), 500);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          ترتيب عناصر القائمة
          <Button variant="outline" size="sm" onClick={resetToDefault}>
            استعادة الافتراضي
          </Button>
        </CardTitle>
        <CardDescription>
          استخدم أزرار الأسهم لإعادة ترتيب عناصر القائمة، وانقر على أيقونة التعديل لتغيير الأسماء
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {items.map((item, index) => (
            <MenuItem
              key={item.id}
              item={item}
              index={index}
              totalItems={items.length}
              onToggleVisibility={handleToggleVisibility}
              onEditTitle={handleEditTitle}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
            />
          ))}
        </div>
        
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700 font-medium">
            💡 نصائح الاستخدام:
          </p>
          <ul className="text-sm text-blue-600 mt-2 space-y-1">
            <li>• استخدم أزرار الأسهم لتحريك العناصر للأعلى والأسفل</li>
            <li>• انقر على أيقونة التعديل لتغيير اسم القسم</li>
            <li>• استخدم أيقونة العين لإخفاء أو إظهار الأقسام</li>
            <li>• انقر على "استعادة الافتراضي" للعودة للإعدادات الأصلية</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
